import {
  Alert,
  Box,
  Stack,
  TextField,
  Text,
  Card,
  Button,
  IconArrow,
  Actions,
  PasswordField,
  IconTick,
  Divider,
} from 'braid-design-system';
import { useState } from 'react';

import { IconDot } from './IconDot.tsx';

import { ApiError, useApi } from '#src/api/index.ts';
import { Logo } from '#src/components/Logo/Logo.tsx';

const MIN_PASSWORD_LENGTH = 15;

interface AccountSetupFormProps {
  token: string;
  setSuccess: (success: boolean) => void;
}

export const AccountSetupForm = ({
  token,
  setSuccess,
}: AccountSetupFormProps) => {
  const [name, setName] = useState<string>('');
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string | undefined>(
    undefined,
  );
  const [loading, setLoading] = useState<boolean>(false);

  const [submitError, setSubmitError] = useState<string | undefined>(undefined);

  const { organizations } = useApi();

  const validateName = () => {
    if (name.trim().length === 0) {
      setNameError('Enter your full name.');
      return false;
    }
    return true;
  };

  const validatePassword = () => {
    if (password.trim().length === 0) {
      setPasswordError('Enter a password.');
      return false;
    }

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match.');
      return false;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(
        `Password must be at least ${MIN_PASSWORD_LENGTH} characters long.`,
      );
      return false;
    }

    if (!hasNoMoreThanTwoIdenticalCharactersInARow) {
      setPasswordError(
        'Password cannot contain more than 2 identical characters in a row.',
      );
      return false;
    }

    if (!(meetsAtLeastThreeCriteria || hasValidLength)) {
      setPasswordError('Password does not meet the required criteria.');
      return false;
    }
    return true;
  };

  const submitForm = async () => {
    setLoading(true);
    const nameIsValid = validateName();
    const passwordIsValid = validatePassword();

    if (nameIsValid && passwordIsValid) {
      try {
        await organizations.finishAccountSetup({ password, name }, token);
        setSuccess(true);
      } catch (e) {
        if (e instanceof ApiError && e.status === 403) {
          setSubmitError(
            'This invitation has already been used or does not exist. Log in or get your admin to send you a new invitation.',
          );
          return;
        }
        setSubmitError('Something went wrong. Try again later.');
      }
    }

    setLoading(false);
  };

  const hasValidLength = password.length >= MIN_PASSWORD_LENGTH;

  const hasLowerCase = /[a-z]/.test(password);
  const hasUpperCase = /[A-Z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecialCharacters = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const hasNoMoreThanTwoIdenticalCharactersInARow = !/([^])\1\1/.test(password);
  const meetsAtLeastThreeCriteria =
    [hasLowerCase, hasUpperCase, hasNumber, hasSpecialCharacters].filter(
      Boolean,
    ).length >= 3;

  return (
    <Stack space="xlarge">
      <Stack space="large" align="center">
        <Logo />
        <Text>
          You have been invited to join an organization on the SEEK Developer
          Dashboard. Enter your name and set a password to finish creating your
          account.
        </Text>
      </Stack>
      <Stack space="xlarge" align="left">
        <Stack space="large">
          <TextField
            id="name-text-field"
            label="Full name"
            value={name}
            characterLimit={100}
            onChange={(e) => {
              setNameError(undefined);
              setName(e.currentTarget.value);
            }}
            tone={nameError ? 'critical' : 'neutral'}
            message={nameError}
          />
          <Divider />
          <Stack space="medium">
            <PasswordField
              autoComplete="new-password"
              id="password-text-field"
              label="Password"
              value={password}
              tone={passwordError ? 'critical' : undefined}
              onChange={(e) => {
                setPasswordError(undefined);
                setPassword(e.currentTarget.value);
              }}
            />
            <PasswordField
              autoComplete="new-password"
              id="confirm-password-text-field"
              label="Confirm password"
              value={confirmPassword}
              tone={passwordError ? 'critical' : undefined}
              onChange={(e) => setConfirmPassword(e.currentTarget.value)}
              message={passwordError}
            />
            {password.length > 0 && (
              <Card>
                <Stack space="small">
                  <Text>Your password must contain:</Text>

                  <Text
                    tone={hasValidLength ? 'positive' : 'neutral'}
                    icon={hasValidLength ? <IconTick /> : <IconDot />}
                  >
                    At least {MIN_PASSWORD_LENGTH} characters
                  </Text>
                  <Text
                    icon={
                      hasNoMoreThanTwoIdenticalCharactersInARow ? (
                        <IconTick />
                      ) : (
                        <IconDot />
                      )
                    }
                    tone={
                      hasNoMoreThanTwoIdenticalCharactersInARow
                        ? 'positive'
                        : 'neutral'
                    }
                  >
                    No more than 2 identical characters in a row
                  </Text>
                  <Text
                    icon={
                      meetsAtLeastThreeCriteria ? <IconTick /> : <IconDot />
                    }
                    tone={meetsAtLeastThreeCriteria ? 'positive' : 'neutral'}
                  >
                    At least 3 of the following:
                  </Text>
                  <Box paddingLeft="gutter">
                    <Stack space="small">
                      <Text
                        icon={hasLowerCase ? <IconTick /> : <IconDot />}
                        tone={hasLowerCase ? 'positive' : 'neutral'}
                      >
                        Lowercase letters (a-z)
                      </Text>
                      <Text
                        icon={hasUpperCase ? <IconTick /> : <IconDot />}
                        tone={hasUpperCase ? 'positive' : 'neutral'}
                      >
                        Uppercase letters (A-Z)
                      </Text>
                      <Text
                        icon={hasNumber ? <IconTick /> : <IconDot />}
                        tone={hasNumber ? 'positive' : 'neutral'}
                      >
                        Numbers (0-9)
                      </Text>
                      <Text
                        icon={hasSpecialCharacters ? <IconTick /> : <IconDot />}
                        tone={hasSpecialCharacters ? 'positive' : 'neutral'}
                      >
                        Special characters (e.g. !@#$%^&*)
                      </Text>
                    </Stack>
                  </Box>
                </Stack>
              </Card>
            )}
          </Stack>
        </Stack>
        <Actions>
          <Button
            icon={<IconArrow direction="right" />}
            loading={loading}
            iconPosition="trailing"
            tone="brandAccent"
            onClick={submitForm}
          >
            Join
          </Button>
        </Actions>
        {submitError ? (
          <Alert tone="critical">
            <Stack space="small">
              <Text>{submitError}</Text>
            </Stack>
          </Alert>
        ) : null}
      </Stack>
    </Stack>
  );
};
